/**
 * sunmiPrinter.ts
 *
 * Bridge al plugin nativo SUNMI de Capacitor.
 * Usa dynamic import() para que @capacitor/core NUNCA se bundle en builds web.
 * En navegador, todas las funciones retornan false / throw sin efecto.
 *
 * Usa el SDK oficial SUNMI (com.sunmi:printerlibrary) vía InnerPrinterManager.
 * La impresora integrada de SUNMI maneja su propio formato — NO usa ESC/POS raw.
 */

const PLUGIN_TIMEOUT_MS = 18000;

export type SunmiPrintResult = { success: boolean; error?: string };

let _plugin: any = null;
let _pluginLoaded = false;

async function getSunmiPlugin(): Promise<any | null> {
  if (!(window as any).Capacitor?.isNativePlatform?.()) return null;
  if (_pluginLoaded) return _plugin;

  try {
    const { registerPlugin } = await import('@capacitor/core');
    _plugin = registerPlugin('SunmiPrinter');
    _pluginLoaded = true;
    console.log('[SunmiPrinter] Plugin registrado OK');
    return _plugin;
  } catch (e) {
    console.error('[SunmiPrinter] Error cargando plugin:', e);
    _pluginLoaded = true;
    _plugin = null;
    return null;
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`${label}: timeout (${ms}ms)`));
    }, ms);
    promise.then(
      (val) => { clearTimeout(timer); resolve(val); },
      (err) => { clearTimeout(timer); reject(err); },
    );
  });
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

/** Checa si el servicio de impresión SUNMI está conectado y listo. */
export async function isSunmiAvailable(): Promise<boolean> {
  const plugin = await getSunmiPlugin();
  if (!plugin) return false;
  try {
    const result: any = await withTimeout(plugin.isAvailable(), PLUGIN_TIMEOUT_MS, 'isAvailable');
    console.log('[SunmiPrinter] isAvailable result:', result);
    return result?.available === true;
  } catch (e) {
    console.warn('[SunmiPrinter] isAvailable falló:', e);
    return false;
  }
}

/**
 * Imprime texto de ticket vía impresora SUNMI integrada (SDK oficial).
 * Envía texto plano — SUNMI lo renderiza internamente vía su API nativa.
 */
export async function printViaSunmi(ticketText: string): Promise<SunmiPrintResult> {
  if (!ticketText || ticketText.trim().length === 0) {
    return { success: false, error: 'No se proporcionó texto para imprimir' };
  }

  const plugin = await getSunmiPlugin();
  if (!plugin) return { success: false, error: 'Plugin SUNMI no disponible' };

  console.log(`[SunmiPrinter] printText: ${ticketText.length} chars`);

  try {
    const result: any = await withTimeout(
      plugin.printText({ text: ticketText }),
      PLUGIN_TIMEOUT_MS,
      'printText',
    );

    if (result?.success === true) {
      console.log('[SunmiPrinter] printText OK');
      return { success: true };
    }

    const stage = result?.stage || 'unknown';
    const err = result?.error || 'sin detalle';
    const error = `SUNMI printText falló [${stage}]: ${err}`;
    console.warn('[SunmiPrinter]', error);
    return { success: false, error };
  } catch (e) {
    const error = getErrorMessage(e);
    console.error('[SunmiPrinter] printText exception:', error);
    return { success: false, error };
  }
}

/**
 * Test de diagnóstico: imprime texto fijo con fecha/hora vía SDK oficial.
 * Usado por el botón "Prueba SUNMI" en LoginPage.
 */
export async function testSunmiPrint(): Promise<{ success: boolean; error?: string; stage?: string; timestamp?: string }> {
  const plugin = await getSunmiPlugin();
  if (!plugin) return { success: false, error: 'No es entorno nativo', stage: 'plugin' };

  try {
    const result: any = await withTimeout(
      plugin.testPrint(),
      PLUGIN_TIMEOUT_MS,
      'testPrint',
    );
    console.log('[SunmiPrinter] testPrint result:', JSON.stringify(result));
    return {
      success: result?.success === true,
      error: result?.error,
      stage: result?.stage,
      timestamp: result?.timestamp,
    };
  } catch (e: any) {
    console.error('[SunmiPrinter] testPrint exception:', e);
    return { success: false, error: e?.message || String(e), stage: 'timeout' };
  }
}
